import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, money, formatErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { TID } from "@/lib/testIds";
import { ArrowRight, Sparkles, ShieldCheck, RotateCcw, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

const WHY = [
  { n: "01", title: "Curated, not crowded", body: "We test 50+ products and ship only the few that earn a spot." },
  { n: "02", title: "Cash on delivery", body: "Pay when it arrives. Try it, then commit." },
  { n: "03", title: "7-day easy returns", body: "Not a fit? Send it back, no awkward questions." },
  { n: "04", title: "Real human support", body: "We reply on WhatsApp within hours, not days." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [store, setStore] = useState({ currency_symbol: "₹" });
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    http.get("/store/products?featured=true&limit=8").then((r) => setProducts(r.data));
    http.get("/store/info").then((r) => setStore(r.data));
  }, []);

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await http.post("/signups", { email, source: "landing" });
      toast.success(data.message || "You're on the list!");
      setEmail("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden pt-12 pb-24 sm:pt-20 sm:pb-32">
        <div className="bls-container grid lg:grid-cols-2 gap-12 items-center">
          <div className="bls-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#e8e6fb] text-[#534ab7] text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="w-3 h-3" /> Now Shipping in India
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              Small upgrades. <br />
              <span className="bls-split-word">Big</span> vibes.
            </h1>
            <p className="mt-6 text-lg text-black/65 max-w-xl leading-relaxed">
              Blesco is a tiny shop of wellness &amp; lifestyle gadgets we actually use. No clutter, no chaos — just things worth keeping on your desk, in your bag, or by your bedside.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/products" className="bls-btn-primary" data-testid={TID.heroPrimaryCta}>
                Shop the lineup <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#why" className="bls-btn-secondary" data-testid={TID.heroSecondaryCta}>
                Why Blesco
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "5+", v: "curated picks" },
                { k: "COD", v: "across India" },
                { k: "7 days", v: "easy returns" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-2xl font-bold tracking-tight">{s.k}</div>
                  <div className="text-xs text-black/55 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative h-[480px] hidden lg:block">
            <div className="absolute inset-0 rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#e8e6fb] via-white to-[#fafaf8]">
              <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#7f77dd] opacity-20 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#2a9d6f] opacity-10 blur-3xl" />
            </div>
            <div className="absolute inset-0 grid grid-cols-2 gap-4 p-8">
              {products.slice(0, 4).map((p, idx) => (
                <Link
                  to={`/product/${p.slug}`}
                  key={p.id}
                  className={`relative rounded-2xl overflow-hidden bg-white shadow-sm border border-black/5 group ${idx === 0 ? "translate-y-4" : idx === 3 ? "-translate-y-4" : ""}`}
                  style={{ animation: `blsFadeUp 0.6s ease-out ${idx * 0.1}s both` }}
                >
                  {p.images?.[0] && (
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                  {p.badge && (
                    <span className="absolute top-3 left-3 bls-tag bls-tag-green">{p.badge}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
        {/* faint background grid */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#fafaf8]" />
      </section>

      {/* DIAGONAL DIVIDER */}
      <div className="bls-diag-divider" />

      {/* PRODUCTS */}
      <section id="products" className="py-20 sm:py-28 bg-[#fafaf8]">
        <div className="bls-container">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#7f77dd]">The lineup</span>
              <h2 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
                Tiny gadgets, <span className="bls-split-word">huge</span> impact.
              </h2>
            </div>
            <Link to="/products" className="hidden sm:inline-flex text-sm font-semibold text-[#534ab7] hover:underline">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <ProductCard key={p.id} p={p} sym={store.currency_symbol} add={add} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="py-20 sm:py-28">
        <div className="bls-container grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#7f77dd]">Why blesco</span>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
              Less browsing. <br /><span className="bls-split-word">More</span> using.
            </h2>
            <p className="mt-6 text-lg text-black/65 leading-relaxed">
              Marketplaces give you a million options. We give you the few that matter — at a price that feels right.
            </p>
          </div>
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {WHY.map((w, i) => (
              <div key={w.n} className="bg-white p-7 rounded-2xl border border-black/5 relative overflow-hidden bls-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="absolute -top-2 -right-2 font-display text-7xl font-bold text-[#e8e6fb] leading-none select-none">
                  {w.n}
                </div>
                <h3 className="font-display text-lg font-bold relative">{w.title}</h3>
                <p className="text-sm text-black/65 mt-2 leading-relaxed relative">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPTURE */}
      <section id="notify" className="py-20 sm:py-28 bg-[#16161f] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#7f77dd] opacity-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#2a9d6f] opacity-10 blur-3xl" />
        <div className="bls-container relative">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#7f77dd]">Get early access</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-white">
              Drops, deals, <span className="text-[#7f77dd]">first.</span>
            </h2>
            <p className="mt-5 text-lg text-white/65">Join the early list. We&apos;ll only email when something genuinely good lands.</p>
            <form onSubmit={submitSignup} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#7f77dd] focus:bg-white/10 transition-colors"
                data-testid={TID.emailSignupInput}
              />
              <button
                type="submit"
                disabled={submitting}
                className="bls-btn-primary disabled:opacity-50"
                data-testid={TID.emailSignupSubmit}
              >
                {submitting ? "Adding…" : "Notify me"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <p className="mt-4 text-xs text-white/40 flex items-center gap-2">
              <Check className="w-3 h-3" /> No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ p, sym, add }) {
  return (
    <div className="bls-card overflow-hidden group" data-testid={`${TID.productCard}-${p.slug}`}>
      <Link to={`/product/${p.slug}`} className="block bls-thumb relative">
        {p.images?.[0] ? (
          <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-[#e8e6fb]" />
        )}
        {p.badge && <span className={`absolute top-3 left-3 bls-tag ${p.badge === "Bestseller" ? "bls-tag-green" : ""}`}>{p.badge}</span>}
      </Link>
      <div className="p-5">
        <Link to={`/product/${p.slug}`} className="block">
          <h3 className="font-display font-bold text-lg leading-tight">{p.name}</h3>
        </Link>
        <p className="text-sm text-black/55 mt-1 line-clamp-2">{p.short_description}</p>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <span className="font-display text-xl font-bold">{money(p.price, sym)}</span>
            {p.compare_at_price && <span className="bls-old-price">{money(p.compare_at_price, sym)}</span>}
          </div>
          <button
            onClick={() => { add(p); toast.success("Added to cart"); }}
            className="px-3 py-2 rounded-full bg-[#16161f] text-white text-xs font-semibold hover:bg-[#534ab7] transition-colors"
            data-testid={`${TID.productAddBtn}-${p.slug}`}
          >
            Add +
          </button>
        </div>
      </div>
    </div>
  );
}
